import React, { useContext, useState, useEffect } from "react";
import { Card, Button, InlineStack, Layout, Text, BlockStack } from "@shopify/polaris";
import { useMutation, useQuery } from "@apollo/client";
// import helpers
import { baseHelper, errorHelper } from "lib/helpers";

// import context
import { PrivateContext } from "lib/context/privateContext";

import { withErrorBoundary, withFeature } from "lib/hoc";

// import constant
import constant from "lib/constant/constant";

import { Banner, SkeletonAnnotated, Toast } from "lib/components";

// import mutations
import { UPDATE_MANAGE_FULFILLMENT_SETTING } from "app/setup/apollo/mutations";
// import queries
import { GET_MANAGE_FULFILLMENT_SETTING } from "app/setup/apollo/queries";

const OperatorManageFulfillmentDetail = () => {
  const { gql } = constant;
  const { currentUser, cms } = useContext(PrivateContext);
  const { _id: currentUserId } = currentUser;
  const [message, setMessage] = useState("");
  const [updateAt, setUpdateAt] = useState(new Date());
  const [isEnabledData, setIsEnabledData] = useState(false);
  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });
  const { data: fulfillSettingData, loading: fulfillSettingLoading, refetch } = useQuery(
    GET_MANAGE_FULFILLMENT_SETTING
  );

  const [updateManageFulfillmentSetting, { loading }] = useMutation(UPDATE_MANAGE_FULFILLMENT_SETTING);
  const contentStatus = isEnabledData ? constant.displayStatus.DISABLE : constant.ENABLE;

  useEffect(() => {
    if (fulfillSettingData) {
      const resData = baseHelper.getResponseData(fulfillSettingData, gql.GET_MANAGE_FULFILLMENT_SETTING);
      const resError = baseHelper.getResponseError(fulfillSettingData, gql.GET_MANAGE_FULFILLMENT_SETTING);
      if (resError) {
        setBanner({
          isOpen: true,
          status: constant.CRITICAL,
          title: resError,
        });
        return;
      }
      if (resData) {
        const { edit, updatedAt } = resData || {};
        setIsEnabledData(edit);
        setUpdateAt(updatedAt);
      }
    }
  }, [fulfillSettingData, setBanner, gql.GET_MANAGE_FULFILLMENT_SETTING]);

  const onCodeSubmit = async (enable) => {
    const formData = {
      edit: enable,
      sellerId: currentUserId,
    };
    try {
      const response = await updateManageFulfillmentSetting({
        variables: {
          input: formData,
        },
      });

      const responseError = baseHelper.getResponseError(response.data, "updateManageFulfillmentSetting");

      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        return;
      }

      if (!enable) {
        setMessage(cms("message.success.disable"));
        setIsEnabledData(!isEnabledData);
      } else {
        setMessage(cms("message.success.enable"));
      }
      refetch();
    } catch (exception) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  const renderBanner = () => {
    const { status, title, isOpen } = banner;
    return (
      <Banner
        isOpen={isOpen}
        status={status}
        title={title}
        onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
      />
    );
  };
  if (fulfillSettingLoading) {
    return <SkeletonAnnotated />;
  }
  return (
    <>
      {banner.isOpen && <Layout.Section>{renderBanner()}</Layout.Section>}
      <Layout.AnnotatedSection
        title={cms("label.section.setting.title")}
        description={cms("label.section.setting.description")}
      >
        <Card>
          <InlineStack gap="400" direction="row" wrap={false} blockAlign="start">
            <BlockStack>
              <Text as="h3" fontWeight="bold">
                {cms("label.section.editFulfillment.title")}
              </Text>
              <Text as="p" tone="subdued">
                {`${baseHelper.lastUpdateDate(updateAt)}`}
              </Text>
              <br />
              <Text as="p">{cms("label.section.editFulfillment.description")}</Text>
            </BlockStack>
            <Button
              variant={!isEnabledData ? "primary" : "tertiary"}
              onClick={() => onCodeSubmit(!isEnabledData)}
              loading={loading}
              buttonType={contentStatus}
              tone={isEnabledData ? "critical" : ""}
              size="large"
            >
              {isEnabledData ? "Disable" : "Enable"}
            </Button>
          </InlineStack>
        </Card>
        <div className="toast">
          <Toast message={message} setToast={setMessage} />
        </div>
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(OperatorManageFulfillmentDetail), {
  feature: constant.MANAGE_FULFILLMENT_DETAIL,
});
